import { useEffect, useMemo, useState } from 'react';
import {
    type CompanyWithPermissionsContextDataFragment,
    type CurrentUserCompaniesDataFragment,
} from '../../../api/fragments';
import { type CompanyContextDataFragment } from '../../../api/fragments/CompanyContextData';
import { useGetAuthenticatedUserCompaniesQuery } from '../../../api/queries/getAuthenticatedUserCompanies';
import useTranslatedString from '../../../utilities/useTranslatedString';
import LoadingElement from '../../LoadingElement';
import { useAccount } from '../AccountContextManager';
import { DealerContextManager } from '../DealerContextManager';
import { CompanyContext, type CompanyContextData, type RefetchUserCompanies } from './Context';

const getTimeZonesByModuleId = (companies: Pick<CompanyContextDataFragment, 'modules' | 'timeZone'>[]) =>
    (companies ?? []).reduce(
        (acc, c) => {
            c.modules?.forEach(m => {
                acc[m.id] = c.timeZone;
            });

            return acc;
        },
        {} as { [key: string]: string }
    );

export type CompanyContextManagerProps = {
    enforcedCompany?: CompanyWithPermissionsContextDataFragment | null;
    children: JSX.Element | React.ReactNode;
};

const Inner = ({
    enforcedCompany = null,
    children,
    user,
    refetch,
}: CompanyContextManagerProps & { user?: CurrentUserCompaniesDataFragment; refetch: RefetchUserCompanies }) => {
    const storageKey = user ? `arc:user-${user.id}:companyId` : null;
    const translated = useTranslatedString();
    // the company to fetch will be coming from router eventually
    const [companyId, setCompanyId] = useState((): string | null => {
        if (enforcedCompany) {
            return enforcedCompany.id;
        }

        if (user) {
            const defaultFromStorage = localStorage.getItem(storageKey);

            if (user.availableCompanies.some(item => item.id === defaultFromStorage)) {
                return defaultFromStorage;
            }

            // there is only one company
            if (user.availableCompanies.length === 1) {
                return user.availableCompanies[0].id;
            }
        }

        return null;
    });

    // persist selected company to local storage
    useEffect(() => {
        if (storageKey) {
            if (companyId) {
                // persist the company id to local storage
                localStorage.setItem(storageKey, companyId);
            } else {
                // remove the company id from local storage
                localStorage.removeItem(storageKey);
            }
        }
    }, [companyId, storageKey]);

    const companies = useMemo((): Array<CompanyWithPermissionsContextDataFragment> => {
        if (enforcedCompany) {
            return [enforcedCompany];
        }

        if (companyId) {
            const selectedCompany = user.availableCompanies.find(item => item.id === companyId);

            if (selectedCompany) {
                return [selectedCompany];
            }
        }

        if (user) {
            return user.availableCompanies.toSorted((a, b) =>
                translated(a.legalName).localeCompare(translated(b.legalName))
            );
        }

        return [];
    }, [user, enforcedCompany, companyId]);

    const allCompaniesSelected = useMemo(() => !companyId, [companyId]);

    const timeZonesByModuleId = useMemo(() => getTimeZonesByModuleId(companies), [companies]);

    const context = useMemo(
        (): CompanyContextData => ({
            refetch,
            replace: setCompanyId,
            userDataFromCompanies: user,
            companies,
            allCompaniesSelected,
            availableCompanies: user?.availableCompanies || [],
            timeZonesByModuleId,
        }),
        [refetch, user, companies, allCompaniesSelected, timeZonesByModuleId]
    );

    return (
        <CompanyContext.Provider value={context}>
            <DealerContextManager company={companies.length === 1 ? companies[0] : null}>
                {children}
            </DealerContextManager>
        </CompanyContext.Provider>
    );
};

export const CompanyContextManager = (props: CompanyContextManagerProps) => {
    const { enforcedCompany } = props;

    // retrieve user information, from which we will look for the available companies
    const user = useAccount(true);
    const { data, loading, refetch } = useGetAuthenticatedUserCompaniesQuery({
        // always check the data from network
        // we will refetch only when needed
        fetchPolicy: 'no-cache',
        variables: {
            ...(enforcedCompany && { companyId: enforcedCompany?.id }),
        },
    });

    useEffect(() => {
        // if user is not the same, get companies again
        if (!loading && data?.currentUser?.id !== user?.id) {
            refetch();
        }
    }, [data?.currentUser?.id, loading, refetch, user?.id]);

    if (loading) {
        return <LoadingElement />;
    }

    return <Inner {...props} refetch={refetch} user={data?.currentUser} />;
};
